import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, Shield, Terminal, 
  Briefcase, BookOpen, Mail, Menu, X,
  Sun, Moon, Award, Cpu 
} from 'lucide-react';
import './Header.css';

const NAV_ITEMS = [
  { id: 'hero', label: 'Home', icon: Home },
  { id: 'skills', label: 'Skills', icon: Shield },
  { id: 'soc', label: 'Work Experience', icon: Briefcase },
  { id: 'tools', label: 'Tools', icon: Terminal },
  { id: 'certifications', label: 'Certifications', icon: Award }, 
  { id: 'deployed-projects', label: 'Projects', icon: Cpu },      
  { id: 'articles', label: 'Articles', icon: BookOpen },
  { id: 'contact', label: 'Contact', icon: Mail },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isDark, setIsDark] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDark(true);
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      setIsDark(false);
      document.documentElement.setAttribute('data-theme', 'light');
    }

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      if (location.pathname === '/') {
        const scrollPosition = window.scrollY + 200; 
        NAV_ITEMS.forEach((item) => {
          const section = document.getElementById(item.id);
          if (section) {
            const top = section.offsetTop;
            const height = section.offsetHeight;
            if (scrollPosition >= top && scrollPosition < top + height) {
              setActiveSection(item.id);
            }
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); 
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]); 

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    const themeString = newTheme ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', themeString);
    localStorage.setItem('theme', themeString);
  };

  const handleNavClick = (id) => {
    setMobileMenuOpen(false); 
    if (location.pathname === '/') {
      scrollToElement(id);
    } else {
      navigate('/', { state: { scrollToId: id } });
    }
  };

  const scrollToElement = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <header className={`hud-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="hud-container">
        
        <div className="hud-logo" onClick={() => handleNavClick('hero')}>
          <span className="logo-bracket">[</span>
          <span className="logo-text">AB</span>
          <span className="logo-bracket">]</span>
        </div>

        <nav className="hud-nav desktop-only">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === '/' && activeSection === item.id;
            return (
              <button 
                key={item.id} 
                className={`hud-btn ${isActive ? 'active' : ''}`}
                onClick={() => handleNavClick(item.id)}
              >
                <Icon size={20} className="hud-icon" />
                <span className="hud-label">{item.label}</span>
                {isActive && <span className="active-dot"></span>}
              </button>
            );
          })}

          <div className="hud-divider"></div>

          <button 
            className="hud-btn theme-toggle" 
            onClick={toggleTheme}
            aria-label="Toggle Theme"
          >
            {isDark ? <Sun size={20} className="hud-icon" /> : <Moon size={20} className="hud-icon" />}
            <span className="hud-label">{isDark ? 'LIGHT_MODE' : 'DARK_MODE'}</span>
          </button>
        </nav>

        <div className="mobile-controls mobile-only">
            <button className="mobile-theme-btn" onClick={toggleTheme}>
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button className="mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
        </div>

        <div className={`mobile-menu mobile-only ${mobileMenuOpen ? 'open' : ''}`}>
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} className="mobile-link" onClick={() => handleNavClick(item.id)}>
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            )
          })}
        </div>

      </div>
    </header>
  );
};

export default Header;